import type { Navigation } from "~/types/api/navigation"
import { RoleName } from "~/types/api/user/role"
import { emmFlowAccessRoles } from "~/utils/api/tools/emm-flow-schemas"

interface MenuItemConfig {
    access: RoleName[];
    label: string;
    icon: string;
    to: string;
    color: string;
    toolTip: string;
    subItems?: Navigation[];
}

// ── Role groups ──────────────────────────────────────────────
const EventModuleRoles = [
    RoleName.webadmin,
    RoleName.webdept,
    RoleName.businessdev,
    RoleName.developer,
    RoleName.superadmin
];

const SEOAndFormsRoles = [
    RoleName.businessdev,
    RoleName.developer,
    RoleName.superadmin,
    RoleName.webadmin,
    RoleName.webdept,
    RoleName.localadmin
];

export const WebformsRoles = [
    RoleName.businessdev,
    RoleName.developer,
    RoleName.superadmin,
    RoleName.webadmin,
    RoleName.webdept,
    RoleName.webformaccess
];

const UsersAndOrders = [
    RoleName.businessdev,
    RoleName.developer,
    RoleName.superadmin,
    RoleName.webadmin,
    RoleName.webdept,
    RoleName.localadmin
];

const ServerRoles = [
    RoleName.developer,
    RoleName.superadmin,
];

const StockRoles = [
    RoleName.businessdev,
    RoleName.developer,
    RoleName.superadmin,
    RoleName.webadmin,
    RoleName.webdept,
    RoleName.accountingdept,
    RoleName.localadmin
];

const PointshopRoles = [
    RoleName.developer,
    RoleName.superadmin,
    RoleName.webadmin,
    RoleName.webdept,
    RoleName.localadmin,
];

const SEOWebpagesRoles = [  
    RoleName.businessdev,
    RoleName.developer,
    RoleName.superadmin,
    RoleName.webadmin,
    RoleName.webdept
];

const DeveloperOnlyRoles = [
    RoleName.developer
];

const toolsMenuItem = (): MenuItemConfig => ({
    access: emmFlowAccessRoles,
    label: "Tools",
    icon: "i-heroicons-wrench-screwdriver",
    to: ``,
    color: "primary",
    toolTip: "Campaign flow tools",
    subItems: [
        {
            label: "EMM Flow",
            icon: "i-heroicons-arrow-path-rounded-square",
            to: `/dashboard/tools/emm-flow`,
            color: "primary",
            toolTip: "Campaign flow codes",
            subItems: [],
        },
    ],
})

const DashboardAdminUserMgmtRoles = [RoleName.superadmin, RoleName.developer]

// ── Filter helper ────────────────────────────────────────────
function filterByAccess(items: MenuItemConfig[], roles: string[]): Navigation[] {
    return items
        .filter(item => item.access.some(role => roles.includes(role)))
        .map(({ access, ...nav }) => nav)
}

// ── Production menu items ──────────────────────────────────── // pragma: allowlist secret
const productionMenuItems = (): MenuItemConfig[] => [ // pragma: allowlist secret
    {
        access: SEOAndFormsRoles,
        label: "SEO",
        icon: "i-heroicons-solid-chart-bar-square",
        to: ``,
        color: "primary",
        toolTip: "SEO",
        subItems: [
            {
                label: "Overview",
                icon: "i-heroicons-book-open",
                to: `/dashboard/seo`,
                color: "primary",
                toolTip: "SEO overview",
                subItems: []
            },
            {
                label: "Keyword Lookup",
                icon: "i-heroicons-magnifying-glass-circle",
                to: `/dashboard/seo/keyword-lookup`,
                color: "primary",
                toolTip: "Lookup keywords and get suggestions",
                subItems: []
            }
        ]
    },
    {
        access: WebformsRoles,
        label: "Webforms",
        icon: "i-heroicons-solid-queue-list",
        to: `/dashboard/webforms`,
        color: "primary",
        toolTip: "Webforms",
        subItems: [],
    },
    {
        access: EventModuleRoles,
        label: "Events",
        icon: "i-heroicons-calendar-days",
        to: `/dashboard/eventmodule`,
        color: "primary",
        toolTip: "Create and manage events",
        subItems: [],
    },
    {
        access: UsersAndOrders,
        label: "Web users",
        icon: "i-heroicons-solid-users",
        to: `/dashboard/webusers`,
        color: "primary",
        toolTip: "Go to users",
        subItems: []
    },
    {
        access: UsersAndOrders,
        label: "Orders",
        icon: "i-heroicons-solid-shopping-cart",
        to: `/dashboard/orders`,
        color: "primary",
        toolTip: "Go to orders",
        subItems: []
    },
    {
        access: ServerRoles,
        label: "Servers",
        icon: "i-heroicons-solid-server",
        to: `/dashboard/servers/overview`,
        color: "primary",
        toolTip: "Server performance",
        subItems: [],
    },
    {
        access: StockRoles,
        label: "Stock",
        icon: "i-heroicons-solid-inbox-stack",
        to: `/dashboard/stock/outofstock`,
        color: "primary",
        toolTip: "Out of stock",
        subItems: [],
    },
    {
        access: PointshopRoles,
        label: "Pointshop",
        icon: "i-heroicons-solid-gift",
        to: ``,
        color: "primary",
        toolTip: "Pointshop",
        subItems: [
            {
                label: "Pointshop Products",
                icon: "i-heroicons-gift",
                to: `/dashboard/pointshop`,
                color: "primary",
                toolTip: "Pointshop products",
                subItems: []
            },
            {
                label: "Point Codes",
                icon: "i-heroicons-qr-code",
                to: ``,
                color: "primary",
                toolTip: "Point code tools",
                subItems: [
                    {
                        label: "Investigate Code",
                        icon: "i-heroicons-magnifying-glass-circle",
                        action: "investigate",
                        color: "primary",
                        toolTip: "Investigate point codes",
                        subItems: []
                    },
                    {
                        label: "Generate Point Codes",
                        icon: "i-heroicons-qr-code",
                        action: "generate",
                        color: "primary",
                        toolTip: "Generate point codes",
                        subItems: []
                    },
                    {
                        label: "Report Suspicious Code",
                        icon: "i-heroicons-shield-exclamation",
                        action: "report",
                        color: "primary",
                        toolTip: "Report suspicious point codes",
                        subItems: []
                    }
                ]
            }
        ],
    },
    {
        access: SEOWebpagesRoles,
        label: "Webpages",
        icon: "i-heroicons-solid-list-bullet",
        to: `/dashboard/webpages`,
        color: "primary",
        toolTip: "Go to webpage list",
        subItems: [],
    },
  {
        access: DashboardAdminUserMgmtRoles,
        label: "Admin users",
        icon: "i-heroicons-shield-check",
        to: `/dashboard/admin/user-management`,
        color: "primary",
        toolTip: "Manage dashboard users and roles",
        subItems: [],
    },
    toolsMenuItem(),
]

// ── Development menu items ───────────────────────────────────
const developmentMenuItems = (clientId: string, websiteId: string): MenuItemConfig[] => [
    {
        access: SEOAndFormsRoles,
        label: "SEO",
        icon: "i-heroicons-solid-chart-bar-square",
        to: ``,
        color: "primary",
        toolTip: "SEO",
        subItems: [
            {
                label: "Overview",
                icon: "i-heroicons-book-open",
                to: `/dashboard/seo`,
                color: "primary",
                toolTip: "SEO overview",
                subItems: []
            },
            {
                label: "Keyword Lookup",
                icon: "i-heroicons-magnifying-glass-circle",
                to: `/dashboard/seo/keyword-lookup`,
                color: "primary",
                toolTip: "Lookup keywords and get suggestions",
                subItems: []
            }
        ]
    },
    {
        access: WebformsRoles,
        label: "Webforms",
        icon: "i-heroicons-solid-queue-list",
        to: `/dashboard/webforms`,
        color: "primary",
        toolTip: "Webforms",
        subItems: [],
    },
    {
        access: EventModuleRoles,
        label: "Events",
        icon: "i-heroicons-calendar-days",
        to: `/dashboard/eventmodule`,
        color: "primary",
        toolTip: "Create and manage events",
        subItems: [],
    },
    {
        access: UsersAndOrders,
        label: "Web users",
        icon: "i-heroicons-solid-users",
        to: `/dashboard/webusers`,
        color: "primary",
        toolTip: "Go to users",
        subItems: []
    },
    {
        access: UsersAndOrders,
        label: "Orders",
        icon: "i-heroicons-solid-shopping-cart",
        to: `/dashboard/orders`,
        color: "primary",
        toolTip: "Go to orders",
        subItems: []
    },
    {
        access: ServerRoles,
        label: "Servers",
        icon: "i-heroicons-solid-server",
        to: `/dashboard/servers/overview`,
        color: "primary",
        toolTip: "Server performance",
        subItems: [],
    },
    {
        access: StockRoles,
        label: "Stock",
        icon: "i-heroicons-solid-inbox-stack",
        to: `/dashboard/stock/outofstock`,
        color: "primary",
        toolTip: "Out of stock",
        subItems: [],
    },
    {
        access: PointshopRoles,
        label: "Pointshop",
        icon: "i-heroicons-solid-gift",
        to: ``,
        color: "primary",
        toolTip: "Pointshop",
        subItems: [
            {
                label: "Pointshop Products",
                icon: "i-heroicons-gift",
                to: `/dashboard/pointshop`,
                color: "primary",
                toolTip: "Pointshop products",
                subItems: []
            },
            {
                label: "Point Codes",
                icon: "i-heroicons-qr-code",
                to: ``,
                color: "primary",
                toolTip: "Point code tools",
                subItems: [
                    {
                        label: "Investigate Code",
                        icon: "i-heroicons-magnifying-glass-circle",
                        action: "investigate",
                        color: "primary",
                        toolTip: "Investigate point codes",
                        subItems: []
                    },
                    {
                        label: "Generate Point Codes",
                        icon: "i-heroicons-qr-code",
                        action: "generate",
                        color: "primary",
                        toolTip: "Generate point codes",
                        subItems: []
                    },
                    {
                        label: "Report Suspicious Code",
                        icon: "i-heroicons-shield-exclamation",
                        action: "report",
                        color: "primary",
                        toolTip: "Report suspicious point codes",
                        subItems: []
                    }
                ]
            }
        ],
    },
    {
        access: SEOWebpagesRoles,
        label: "Webpages",
        icon: "i-heroicons-solid-list-bullet",
        to: `/dashboard/webpages`,
        color: "primary",
        toolTip: "Go to webpage list",
        subItems: []
    },
    {
        access: DashboardAdminUserMgmtRoles,
        label: "Admin users",
        icon: "i-heroicons-shield-check",
        to: `/dashboard/admin/user-management`,
        color: "primary",
        toolTip: "Manage dashboard users and roles",
        subItems: [],
    },
    toolsMenuItem(),
    {
        access: DeveloperOnlyRoles,
        label: "In development",
        icon: "i-heroicons-beaker",
        to: ``,
        color: "primary",
        toolTip: "In development",
        subItems: [
            {
                label: "E-mail",
                icon: "i-heroicons-solid-envelope",
                to: `/dashboard/client-${clientId}/mail-builder`,
                color: "primary",
                toolTip: "Mail builder",
                subItems: [],
            },
            {
                label: "Website",
                icon: "i-heroicons-solid-globe-alt",
                to: `/dashboard/client-${clientId}/website-345`,
                color: "primary",
                toolTip: "Go to website",
                subItems: []
            },
            {
                label: "Web pages ORIGINAL",
                icon: `i-heroicons-solid-list-bullet`,
                to: `/dashboard/client-${clientId}/website-${websiteId}/webpages`,
                color: "primary",
                toolTip: "Go to webpage list",
                subItems: []
            },
            {
                label: "Web pages V2",
                icon: `i-heroicons-solid-list-bullet`,
                to: `/dashboard/webpages`,
                color: "primary",
                toolTip: "Go to webpage list",
                subItems: []
            },
            {
                label: "Home",
                icon: "i-heroicons-solid-home",
                to: `/dashboard`,
                color: "primary",
                toolTip: "Dashboard",
                subItems: [],
            },
            {
                label: "dashboard",
                icon: "i-heroicons-share",
                to: `/dashboard/client-${clientId}/screens`,
                color: "primary",
                toolTip: "Client",
                subItems: [],
            },
            {
                label: "Install windows XP",
                icon: "i-heroicons-home",
                to: "/dashboard",
                color: "primary",
                toolTip: "Install windows XP",
                subItems: [],
            },
            {
                label: "Install skirt kit on your car",
                icon: "i-heroicons-home",
                to: "/dashboard",
                color: "primary",
                toolTip: "Install skirt kit on your car",
                subItems: [],
            },
            {
                label: "Translations",
                icon: "i-heroicons-solid-translate",
                to: `/dashboard/translations`,
                color: "primary",
                toolTip: "Translations",
                subItems: [],
            },
        ]
    },
]

// ── Exported functions (same signatures as before) ───────────
export const productionMainMenuLinks = (_clientId: string, _websiteId: string, role: string[] = ['localadmin']): Navigation[] => { // pragma: allowlist secret
    return filterByAccess(productionMenuItems(), role) // pragma: allowlist secret
}

export const developmentMainMenuLinks = (clientId: string, websiteId: string, role: string[] = ['localadmin']): Navigation[] => {
    return filterByAccess(developmentMenuItems(clientId, websiteId), role)
}
